import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
	ButtonComponent,
	HistoryService,
	RoutedModalBaseComponent,
	SlottedModalComponent,
} from '@woolworthsnz/styleguide';
import { ProductResponse } from '@woolworthsnz/trader-api';
import { ProductService } from '../../services/product.service';
import { ProductState } from '../../services/product.state';
import { NgIf } from '@angular/common';
import { ProductSource } from '../../models/tealium-events';
import { Subject, takeUntil } from 'rxjs';
import { TealiumUtagService } from '@woolworthsnz/analytics';

export enum ActionOnPriceChange {
	accept = 'accept',
	cancel = 'cancel edit',
	close = 'X',
}

@Component({
	selector: 'product-changed-price-modal',
	templateUrl: './changed-price-modal.component.html',
	styleUrls: ['./changed-price-modal.component.scss'],
	standalone: true,
	imports: [NgIf, SlottedModalComponent, ButtonComponent],
	providers: [ProductService],
})
export class ChangedPriceModalComponent extends RoutedModalBaseComponent implements OnDestroy {
	product: ProductResponse;
	quantity: number;
	priceChangeDetails: any;
	source: ProductSource;
	updateProductDetails = false;
	showActionButtons = true;
	private destroyed$: Subject<boolean> = new Subject();

	public get actionOnPriceChange(): typeof ActionOnPriceChange {
		return ActionOnPriceChange;
	}

	constructor(
		protected router: Router,
		protected historyService: HistoryService,
		private productService: ProductService,
		protected activedRoute: ActivatedRoute,
		private tealiumUtagService: TealiumUtagService
	) {
		super(router, activedRoute, historyService);

		const state = this.router.getCurrentNavigation()?.extras.state;

		if (state) {
			const {
				// eslint-disable-next-line @typescript-eslint/no-shadow
				productService,
				priceChangeDetails,
				quantity,
				source,
				updateProductDetails,
				showActionButtons,
			} = state;
			this.productService = productService;
			this.priceChangeDetails = priceChangeDetails;
			this.quantity = quantity;
			this.source = source;
			this.updateProductDetails = updateProductDetails;
			this.showActionButtons = showActionButtons;

			if (this.productService) {
				this.productService.state$.pipe(takeUntil(this.destroyed$)).subscribe((productState: ProductState) => {
					this.product = productState?.product as ProductResponse;
				});
			}
		}
	}
	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	formatPrice(unformattedPrice: number): { dollars: number; cents: number } | { dollars: string; cents: string } {
		if (!unformattedPrice) {
			return { dollars: 0, cents: 0 };
		}

		const price = (<number>unformattedPrice).toFixed(2).split('.');

		return {
			dollars: price[0] || '0',
			cents: price[1] || '00',
		};
	}

	getFormattedPrice(unformattedPrice: number): string | null {
		if (
			this.product.supportsBothEachAndKgPricing &&
			this.product.selectedPurchasingUnit === ProductResponse.SelectedPurchasingUnitEnum.Each
		) {
			return null;
		}

		const { dollars, cents } = this.formatPrice(unformattedPrice);
		return `$${dollars}.${cents} ${this.product.unit === ProductResponse.UnitEnum.Kg ? '/ kg' : 'each'}`;
	}

	handleAcceptEdits(): void {
		this.trackEvent(ActionOnPriceChange.accept);
		this.productService.addOrUpdateTrolley(this.quantity, this.source, this.updateProductDetails, false);
		super.close();
	}

	handleCancelEdits(trackEventName: string): void {
		this.trackEvent(trackEventName);
		super.close();
	}

	trackEvent(value: string): void {
		const notificationEventName =
			this.priceChangeDetails.requestedItemOldSellPrice < this.priceChangeDetails.requestedItemNewSellPrice
				? 'item price change - increase'
				: 'item price change - decrease';

		const priceChangeTealium = {
			tealium_event: 'view_notification',
			notification_type: 'pop-up',
			notification_name: notificationEventName,
			notification_link_text: value,
		};
		this.tealiumUtagService.view({ ...priceChangeTealium });
	}
}
